import { useState } from 'react'
import { useNavigate } from 'react-router'
import { formatDistanceToNow } from 'date-fns'
import { type ColumnDef } from '@tanstack/react-table'
import { CheckCircle2, Loader2, Mail } from 'lucide-react'
import { useAdminFeedbackList, useUpdateConversationStatus } from '@/hooks/useAdminFeedback'
import { useAdminFeedbackRealtime } from '@/hooks/useAdminFeedbackRealtime'
import { type AdminConversation } from '@/lib/api/admin-feedback'
import { DataTable } from '@/components/shared/DataTable'
import { DataTableColumnHeader } from '@/components/shared/DataTableColumnHeader'
import { PageHeader } from '@/components/shared/PageHeader'
import { StatusBadge, CategoryBadge, PriorityBadge } from './ConversationStatusBadge'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'

const RESOLVABLE_STATUSES = new Set(['open', 'waiting_on_user', 'waiting_on_admin'])

function ResolveRowAction({ conversation }: { conversation: AdminConversation }) {
  const statusMutation = useUpdateConversationStatus()
  if (!RESOLVABLE_STATUSES.has(conversation.status)) return null

  const resolve = (notifyUser: boolean) =>
    statusMutation.mutate({ conversationId: conversation.id, status: 'resolved', notifyUser })

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          title="Mark resolved"
          disabled={statusMutation.isPending}
          onClick={(e) => e.stopPropagation()}
        >
          {statusMutation.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <CheckCircle2 className="h-4 w-4 text-muted-foreground hover:text-emerald-600" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
        <DropdownMenuItem onSelect={() => resolve(false)}>
          <CheckCircle2 className="mr-2 h-4 w-4" /> Resolve (no email)
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => resolve(true)}>
          <Mail className="mr-2 h-4 w-4" /> Resolve &amp; notify user
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

const columns: ColumnDef<AdminConversation, unknown>[] = [
  {
    accessorKey: 'status',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
    cell: ({ row }) => <StatusBadge status={row.original.status} />,
    size: 130,
  },
  {
    id: 'resolve',
    header: '',
    cell: ({ row }) => <ResolveRowAction conversation={row.original} />,
    size: 40,
  },
  {
    id: 'user',
    accessorFn: (row) => row.profiles?.display_name || row.user_email,
    header: ({ column }) => <DataTableColumnHeader column={column} title="User" />,
    cell: ({ row }) => (
      <div className="min-w-0 flex items-center gap-2">
        <div className="min-w-0">
          <div className="text-sm font-medium truncate">
            {row.original.profiles?.display_name ?? row.original.user_email}
          </div>
          {row.original.profiles?.display_name && (
            <div className="text-xs text-muted-foreground truncate">{row.original.user_email}</div>
          )}
        </div>
        {row.original.profiles?.is_test === true && (
          <Badge variant="secondary" className="text-[10px] shrink-0">
            Internal
          </Badge>
        )}
        {(row.original.user_open_threads ?? 0) > 0 && (
          <Badge
            variant="outline"
            className="text-[10px] shrink-0"
            title="Other threads from this user that are not resolved or archived"
          >
            {row.original.user_open_threads} other open
          </Badge>
        )}
      </div>
    ),
    size: 220,
  },
  {
    id: 'subject',
    accessorFn: (row) => row.subject || '',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Subject" />,
    cell: ({ row }) => (
      <span className="text-sm truncate block max-w-[300px]">
        {row.original.subject || '(no subject)'}
      </span>
    ),
  },
  {
    accessorKey: 'category',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Category" />,
    cell: ({ row }) => <CategoryBadge category={row.original.category} />,
    size: 100,
  },
  {
    accessorKey: 'priority',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Priority" />,
    cell: ({ row }) => <PriorityBadge priority={row.original.priority} />,
    size: 90,
  },
  {
    accessorKey: 'admin_unread',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Unread" />,
    cell: ({ row }) =>
      row.original.admin_unread > 0 ? (
        <span className="inline-flex items-center justify-center h-5 min-w-[20px] rounded-full bg-destructive text-destructive-foreground text-xs font-bold px-1.5">
          {row.original.admin_unread}
        </span>
      ) : null,
    size: 70,
  },
  {
    accessorKey: 'last_message_at',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Last Message" />,
    sortingFn: (a, b) => {
      const av = a.original.last_message_at ? new Date(a.original.last_message_at).getTime() : 0
      const bv = b.original.last_message_at ? new Date(b.original.last_message_at).getTime() : 0
      return av - bv
    },
    cell: ({ row }) =>
      row.original.last_message_at ? (
        <span className="text-xs text-muted-foreground whitespace-nowrap">
          {formatDistanceToNow(new Date(row.original.last_message_at), { addSuffix: true })}
        </span>
      ) : (
        <span className="text-xs text-muted-foreground">—</span>
      ),
    size: 120,
  },
]

export function FeedbackListPage() {
  useAdminFeedbackRealtime()
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [statusFilter] = useState<string[]>([])
  const [categoryFilter] = useState<string[]>([])
  const [page, setPage] = useState(1)

  // Load the full list (capped at 100 by the edge function) and paginate + sort
  // client-side — server-side pagination defeats client-side sorting otherwise.
  const { data, isLoading } = useAdminFeedbackList({
    page: 1,
    pageSize: 100,
    search,
    statusFilter: statusFilter.length > 0 ? statusFilter : undefined,
    categoryFilter: categoryFilter.length > 0 ? categoryFilter : undefined,
  })

  return (
    <div className="space-y-6">
      <PageHeader title="Feedback" description="User feedback and support conversations" />

      <DataTable
        columns={columns}
        data={data?.conversations ?? []}
        isLoading={isLoading}
        emptyMessage="No feedback conversations yet."
        onRowClick={(row) => navigate(`/feedback/${row.id}`)}
        toolbar={
          <div className="flex items-center gap-2">
            <Input
              placeholder="Search by user or subject..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                setPage(1)
              }}
              className="max-w-sm h-9"
            />
          </div>
        }
        pagination={{ pageIndex: page - 1, pageSize: 25 }}
        onPaginationChange={(updater) => {
          const next = typeof updater === 'function'
            ? updater({ pageIndex: page - 1, pageSize: 25 })
            : updater
          setPage(next.pageIndex + 1)
        }}
      />
    </div>
  )
}
